import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading } from "@/components/shared";
import { toast } from "sonner";
import { Briefcase } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DEPARTMENTS = ["Engineering", "Product", "Marketing", "Compliance", "Payments", "Data", "Operations"];
const MODELS = ["Remote", "Hybrid", "Onsite"];

export default function PostJob() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [markets, setMarkets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    company_name: "", title: "", department: "Operations", market: "", working_model: "Hybrid",
    salary_min: "", salary_max: "", salary_currency: "EUR", salary_period: "year",
    description: "", apply_url: "",
  });

  useEffect(() => {
    if (user === false) { nav("/login"); return; }
    if (user && user.role !== "admin" && user.role !== "moderator") { nav("/jobs"); return; }
    api.get("/markets").then(({ data }) => setMarkets(data)).catch(() => {});
  }, [user, nav]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.company_name && form.title && form.market && form.description
    && Number(form.salary_min) > 0 && Number(form.salary_max) >= Number(form.salary_min);

  const submit = async () => {
    if (!valid) { toast.error("Fill in all fields. Max salary must be at least the minimum."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/jobs", {
        ...form, salary_min: Number(form.salary_min), salary_max: Number(form.salary_max),
      });
      toast.success("Vacancy posted.");
      nav(`/job/${data.id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (user === null) return <Loading />;

  return (
    <main className="mx-auto max-w-2xl px-5 py-10 lg:px-8">
      <p className="eyebrow mb-2 flex items-center gap-2"><Briefcase className="h-4 w-4" /> Post a vacancy</p>
      <h1 className="text-3xl font-bold">List an open role</h1>
      <p className="mt-3 text-slate-400">Every vacancy on iGamingTruth must show a real salary range. That is the point.</p>

      <div className="card-surface mt-8 space-y-4 p-6 sm:p-8">
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label>Company name</Label><Input data-testid="job-company" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className="mt-1.5 border-[#DDDAD1] bg-ink-900" /></div>
          <div><Label>Role title</Label><Input data-testid="job-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5 border-[#DDDAD1] bg-ink-900" /></div>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div><Label>Department</Label>
            <Select value={form.department} onValueChange={(v) => set("department", v)}>
              <SelectTrigger data-testid="job-dept" className="mt-1.5 border-[#DDDAD1] bg-ink-900"><SelectValue /></SelectTrigger>
              <SelectContent className="border-[#DDDAD1] bg-ink-900">{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Hub</Label>
            <Select value={form.market} onValueChange={(v) => set("market", v)}>
              <SelectTrigger data-testid="job-market-select" className="mt-1.5 border-[#DDDAD1] bg-ink-900"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent className="border-[#DDDAD1] bg-ink-900">
                {markets.map((m) => <SelectItem key={m.market} value={m.market}>{m.market}</SelectItem>)}
                {["Malta", "Gibraltar", "Isle of Man", "Cyprus", "United Kingdom", "Estonia", "Remote"].map((m) => <SelectItem key={`x-${m}`} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Work style</Label>
            <Select value={form.working_model} onValueChange={(v) => set("working_model", v)}>
              <SelectTrigger data-testid="job-model-select" className="mt-1.5 border-[#DDDAD1] bg-ink-900"><SelectValue /></SelectTrigger>
              <SelectContent className="border-[#DDDAD1] bg-ink-900">{MODELS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Salary range (required)</Label>
          <div className="mt-1.5 flex gap-2">
            <Select value={form.salary_currency} onValueChange={(v) => set("salary_currency", v)}>
              <SelectTrigger data-testid="job-currency" className="w-24 border-[#DDDAD1] bg-ink-900"><SelectValue /></SelectTrigger>
              <SelectContent className="border-[#DDDAD1] bg-ink-900">{["EUR", "USD", "GBP"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="job-salary-min" type="number" min="1" value={form.salary_min} onChange={(e) => set("salary_min", e.target.value)} placeholder="Min" className="flex-1 border-[#DDDAD1] bg-ink-900" />
            <Input data-testid="job-salary-max" type="number" min="1" value={form.salary_max} onChange={(e) => set("salary_max", e.target.value)} placeholder="Max" className="flex-1 border-[#DDDAD1] bg-ink-900" />
            <Select value={form.salary_period} onValueChange={(v) => set("salary_period", v)}>
              <SelectTrigger data-testid="job-period" className="w-28 border-[#DDDAD1] bg-ink-900"><SelectValue /></SelectTrigger>
              <SelectContent className="border-[#DDDAD1] bg-ink-900"><SelectItem value="year">per year</SelectItem><SelectItem value="month">per month</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div><Label>Description</Label><Textarea data-testid="job-description" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="What the role involves, the team, requirements…" className="mt-1.5 min-h-32 border-[#DDDAD1] bg-ink-900" /></div>
        <div><Label>Apply URL (optional)</Label><Input data-testid="job-apply-url" value={form.apply_url} onChange={(e) => set("apply_url", e.target.value)} placeholder="https://…" className="mt-1.5 border-[#DDDAD1] bg-ink-900" /></div>
        <button data-testid="job-submit" onClick={submit} disabled={busy} className="btn-teal text-sm disabled:opacity-50">{busy ? "Posting…" : "Publish vacancy"}</button>
      </div>
    </main>
  );
}
